
/**
 * Service for handling file operations and text extraction
 */

/**
 * Extract text content from a file (PDF or TXT)
 * @param file The file to extract text from
 * @returns A promise resolving to the extracted text
 */
export const extractTextFromFile = async (file: File): Promise<string> => {
  // For text files, simply read the text content
  if (file.type === 'text/plain') {
    return await file.text();
  }
  
  // For PDF files, we'll use a simple text extraction approach
  // Note: This is a basic implementation. For production, consider using a more robust PDF parsing library.
  if (file.type === 'application/pdf') {
    const arrayBuffer = await file.arrayBuffer();
    const text = await extractTextFromPDF(arrayBuffer);
    return text;
  }
  
  throw new Error('Unsupported file type. Please upload a PDF or TXT file.');
};

/**
 * Basic PDF text extraction
 * This is a simplified implementation that extracts text from PDF documents
 * For a production app, consider using a library like pdf.js
 */
const extractTextFromPDF = async (arrayBuffer: ArrayBuffer): Promise<string> => {
  // Convert the array buffer to a text string
  // This is a very basic approach and won't work for all PDFs,
  // but serves as a placeholder for the functionality
  const uint8Array = new Uint8Array(arrayBuffer);
  let text = '';
  
  // Look for text patterns in the PDF
  // This is a very basic approach - it looks for text between parentheses
  // which is a common pattern in some PDFs, but won't work reliably for all
  let inString = false;
  let currentString = '';
  
  for (let i = 0; i < uint8Array.length; i++) {
    const byte = uint8Array[i];
    const char = String.fromCharCode(byte);
    
    if (char === '(' && !inString) {
      inString = true;
      currentString = '';
    } else if (char === ')' && inString) {
      inString = false;
      text += currentString + ' ';
    } else if (inString) {
      currentString += char;
    }
  }
  
  // Basic cleanup
  text = text.replace(/\\n/g, '\n').replace(/\\\(/g, '(').replace(/\\\)/g, ')');
  
  // If no text was found, return a message
  if (!text.trim()) {
    return "Unable to extract text from this PDF. Please try a text file instead.";
  }
  
  return text;
};
